import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeftOnRectangleIcon,
  BanknotesIcon,
  Bars3Icon,
  BellIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  MoonIcon,
  MusicalNoteIcon,
  Squares2X2Icon,
  SunIcon,
  UserGroupIcon,
  ArrowUpTrayIcon,
  SparklesIcon,
  ShoppingCartIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";
import { Company, User } from "../types";
import { CommandPalette, CommandAction } from "./CommandPalette";
import { SystemMonitor } from "./SystemMonitor";
import { useTheme } from "../hooks/useTheme";
import { apiService } from "../services/apiService";

export interface NavItem {
  id: string;
  name: string;
  icon: React.ComponentType<{ className?: string }>;
}

export interface ProjectOption {
  id: string;
  name: string;
  color?: string;
  slug?: string;
}

interface AiHeaderStatus {
  modelDisplayName?: string;
  model?: string;
  provider?: string;
  configured?: boolean;
  ok?: boolean;
  latencyMs?: number | null;
}

interface AppShellProps {
  children: React.ReactNode;
  navItems: NavItem[];
  activeTab: string;
  onNavigate: (tabId: string) => void;
  currentUser: User | null;
  companies: Company[];
  currentCompanyId: string;
  onSwitchCompany: (companyId: string) => void;
  projects?: ProjectOption[];
  currentProjectId?: string | null;
  onSwitchProject?: (projectId: string | null) => void;
  onLogout: () => void;
  onNewInvoice: () => void;
  onOpenSettings: () => void;
  commandActions: CommandAction[];
  /** @deprecated AppShell obtiene el estado IA vía API */
  isAIConnected?: boolean;
  onOpenEmail?: () => void;
  onOpenAssistant?: () => void;
  assistantOpen?: boolean;
}

function AiStatusPill() {
  const [ai, setAi] = useState<AiHeaderStatus | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await apiService.getAiStatus();
        if (!cancelled && res.data) {
          setAi({
            modelDisplayName: (res.data as { modelDisplayName?: string }).modelDisplayName,
            model: res.data.model,
            provider: res.data.provider,
            configured: res.data.configured,
            ok: res.data.ok,
            latencyMs: (res.data as { latencyMs?: number | null }).latencyMs ?? null,
          });
        }
      } catch {
        if (!cancelled) setAi({ configured: false, ok: false });
      }
    };
    load();
    const t = setInterval(load, 120000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  if (!ai) return null;

  const online = !!ai.configured && ai.ok !== false;
  const dot = online ? "bg-emerald-500" : ai.configured ? "bg-amber-400" : "bg-slate-400";
  const label = ai.modelDisplayName || ai.model || "IA";

  return (
    <div
      className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#E6E9F0] dark:border-[#30363d] bg-white dark:bg-[#161b22] px-2.5 py-1 text-[11px] text-[#425466] dark:text-slate-300"
      title={
        ai.provider
          ? `${label} · ${ai.provider}${ai.latencyMs != null ? ` · ${ai.latencyMs}ms` : ""}`
          : label
      }
    >
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${dot}`} aria-hidden />
      <span className="font-medium text-[#0A2540] dark:text-slate-100 truncate max-w-[120px]">{label}</span>
      {ai.provider && (
        <span className="text-[#8898aa] dark:text-slate-500 capitalize hidden md:inline">{ai.provider}</span>
      )}
      {ai.latencyMs != null && ai.latencyMs > 0 && (
        <span className="text-[#8898aa] tabular-nums hidden lg:inline">{ai.latencyMs}ms</span>
      )}
    </div>
  );
}

export const AppShell: React.FC<AppShellProps> = ({
  children,
  navItems,
  activeTab,
  onNavigate,
  currentUser,
  companies,
  currentCompanyId,
  onSwitchCompany,
  projects = [],
  currentProjectId = null,
  onSwitchProject,
  onLogout,
  onNewInvoice,
  onOpenSettings,
  commandActions,
  onOpenEmail,
  onOpenAssistant,
  assistantOpen = false,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen bg-[#F6F9FC] dark:bg-[#0d1117] text-[#0A2540] dark:text-[#e6edf3] flex">
      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} actions={commandActions} />

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 z-40 h-screen flex flex-col border-r border-[#E6E9F0] dark:border-[#21262d] bg-white dark:bg-[#161b22] transition-all duration-200 ${
          sidebarCollapsed ? "w-[68px]" : "w-[220px]"
        } ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        {/* Logo area */}
        <div className="flex items-center h-14 px-4 border-b border-[#E6E9F0] dark:border-[#21262d]">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div className="w-7 h-7 rounded-lg bg-[#635BFF] flex items-center justify-center shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="font-semibold text-sm tracking-tight text-[#0A2540] dark:text-[#e6edf3] truncate">FactuApp</span>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-7 h-7 rounded-lg bg-[#635BFF] flex items-center justify-center mx-auto">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" className="text-white">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
          {!sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed((c) => !c)}
              className="hidden lg:flex p-1.5 rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-[#8898aa] transition-colors"
              aria-label="Colapsar menú"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
          )}
          {sidebarCollapsed && (
            <button
              type="button"
              onClick={() => setSidebarCollapsed((c) => !c)}
              className="hidden lg:flex p-1.5 rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-[#8898aa] transition-colors absolute right-1"
              aria-label="Expandir menú"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  onNavigate(item.id);
                  setMobileOpen(false);
                }}
                title={sidebarCollapsed ? item.name : undefined}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150 ${
                  active
                    ? "bg-[#EEF0FF] text-[#635BFF] dark:bg-[#635BFF]/15 dark:text-[#a5a9ff]"
                    : "text-[#425466] dark:text-[#8b949e] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#0A2540] dark:hover:text-[#e6edf3]"
                }`}
              >
                <Icon className={`h-4.5 w-4.5 h-[18px] w-[18px] shrink-0 ${active ? "text-[#635BFF] dark:text-[#a5a9ff]" : ""}`} />
                {!sidebarCollapsed && <span className="truncate">{item.name}</span>}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-3 border-t border-[#E6E9F0] dark:border-[#21262d]">
          {!sidebarCollapsed && currentUser && (
            <div className="flex items-center gap-2 px-2 py-1.5 mb-1">
              <div className="w-6 h-6 rounded-full bg-[#635BFF]/10 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-semibold text-[#635BFF]">
                  {currentUser.name?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs font-medium text-[#0A2540] dark:text-[#e6edf3] truncate">{currentUser.name}</p>
                <p className="text-[10px] text-[#8898aa] truncate capitalize">{currentUser.role}</p>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-[#8898aa] hover:bg-red-50 dark:hover:bg-red-950/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <ArrowLeftOnRectangleIcon className="h-[18px] w-[18px] shrink-0" />
            {!sidebarCollapsed && <span className="text-sm">Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-30 bg-black/30 backdrop-blur-sm lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 h-14 border-b border-[#E6E9F0] dark:border-[#21262d] bg-white/90 dark:bg-[#161b22]/90 backdrop-blur-xl flex items-center gap-2 sm:gap-3 px-4">
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] text-[#425466]"
            onClick={() => setMobileOpen(true)}
          >
            <Bars3Icon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex-1 max-w-sm flex items-center gap-2 rounded-lg border border-[#E6E9F0] dark:border-[#30363d] bg-[#F6F9FC] dark:bg-[#0d1117] px-3 py-1.5 text-sm text-[#8898aa] hover:border-[#635BFF]/40 focus:outline-none transition-colors"
          >
            <MagnifyingGlassIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">Buscar…</span>
            <kbd className="ml-auto hidden sm:inline text-[10px] border border-[#E6E9F0] dark:border-[#30363d] rounded px-1.5 py-0.5 text-[#8898aa] font-sans">
              ⌘K
            </kbd>
          </button>
          <select
            value={currentCompanyId}
            onChange={(e) => onSwitchCompany(e.target.value)}
            className="hidden sm:block text-xs border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-2 py-1.5 bg-white dark:bg-[#161b22] text-[#0A2540] dark:text-[#e6edf3] max-w-[140px] focus:outline-none focus:border-[#635BFF] transition-colors"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {projects.length > 0 && onSwitchProject && (
            <select
              value={currentProjectId || ""}
              onChange={(e) => onSwitchProject(e.target.value || null)}
              className="hidden md:block text-xs border border-[#E6E9F0] dark:border-[#30363d] rounded-lg px-2 py-1.5 bg-white dark:bg-[#161b22] text-[#0A2540] dark:text-[#e6edf3] max-w-[150px] focus:outline-none focus:border-[#635BFF] transition-colors"
              title="Proyecto"
            >
              <option value="">Todos los proyectos</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <div className="flex items-center gap-0.5">
            {onOpenEmail && (
              <button
                type="button"
                onClick={onOpenEmail}
                className={`p-2 rounded-lg transition-colors ${
                  activeTab === "email"
                    ? "text-[#635BFF] bg-[#635BFF]/10"
                    : "text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#425466]"
                }`}
                aria-label="Email"
                title="Email"
              >
                <EnvelopeIcon className="h-5 w-5" />
              </button>
            )}
            {onOpenAssistant && (
              <button
                type="button"
                onClick={onOpenAssistant}
                className={`p-2 rounded-lg transition-colors ${
                  assistantOpen
                    ? "text-[#635BFF] bg-[#635BFF]/10 ring-1 ring-[#635BFF]/20"
                    : "text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#635BFF]"
                }`}
                aria-label="Asistente IA"
                title="Asistente de facturación"
              >
                <SparklesIcon className="h-5 w-5" />
              </button>
            )}
          </div>
          <motion.div className="hidden md:flex items-center">
            <SystemMonitor variant="inline" />
          </motion.div>
          <AiStatusPill />
          <button
            type="button"
            className="p-2 rounded-lg text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#425466] transition-colors"
            aria-label="Notificaciones"
          >
            <BellIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className="p-2 rounded-lg text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#425466] transition-colors"
            aria-label="Modo oscuro"
          >
            {isDark ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
          </button>
          <button
            type="button"
            onClick={onOpenSettings}
            className="p-2 rounded-lg text-[#8898aa] hover:bg-[#F6F9FC] dark:hover:bg-[#21262d] hover:text-[#425466] transition-colors"
          >
            <Cog6ToothIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onNewInvoice}
            className="hidden sm:inline-flex items-center gap-1.5 rounded-lg bg-[#635BFF] text-white text-xs font-semibold px-3 py-2 hover:bg-[#4F46E5] shadow-sm transition-colors"
          >
            Nueva factura
          </button>
        </header>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export const buildDefaultNavItems = (
  showEntik: boolean,
  showWooCommerce = false,
  showKitDigital = false
): NavItem[] => [
  { id: "dashboard", name: "Panel", icon: Squares2X2Icon },
  { id: "invoices", name: "Facturas", icon: DocumentTextIcon },
  { id: "clients", name: "Clientes", icon: UserGroupIcon },
  { id: "import", name: "Importar", icon: ArrowUpTrayIcon },
  { id: "work-expenses", name: "Gastos", icon: BanknotesIcon },
  { id: "gestoria", name: "Gestoría", icon: BriefcaseIcon },
  { id: "auto-billing", name: "Auto-facturación", icon: CalendarDaysIcon },
  ...(showEntik ? [{ id: "entik-reservas", name: "Entik Records", icon: MusicalNoteIcon }] : []),
  ...(showKitDigital ? [{ id: "kit-digital", name: "Kit Digital", icon: ComputerDesktopIcon }] : []),
  ...(showWooCommerce ? [{ id: "woocommerce", name: "Tienda online", icon: ShoppingCartIcon }] : []),
  { id: "email", name: "Email", icon: EnvelopeIcon },
  { id: "settings", name: "Ajustes", icon: Cog6ToothIcon },
];
